import { formatDecimal, formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { buildPivot, PivotMeasure, PivotNode } from "../../../dashboard-framework/pivot";
import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import {
    CLIENT_TYPE_LABELS,
    clientTypeOfAge,
    coarseDisorderGroupLabel,
    coarseDisorderGroupOf,
    disorderDrillLabelOf,
} from "../shared/disorderGroups";
import type { ClientTypeKey, CoarseDisorderGroupKey } from "../shared/disorderGroups";
import { FINISHED_STATUSES, SUCCESS_STATUSES, successRatePct } from "../shared/sharedMetrics";

// Tratamentos terminados por gestora de caso — visão GLOBAL da empresa (sessions-treatments).
// Ao contrário das tabelas do clinic-service-team-kpis, esta NÃO separa por serviço (não há porta
// "≥1 sessão NFB/Psicologia realizada") nem exclui os tratamentos em que a gestora é também psicóloga:
// conta TODOS os tratamentos terminados, atribuídos à gestora de caso (dono do tratamento); tratamentos
// sem gestora contam na linha "Sem gestora de caso". Lógica pura própria (fork) — não importa de
// teamKpisOutcomes.ts para não acoplar/alterar esse dashboard.

export const NO_OWNER_LABEL = "Sem gestora de caso";
const ACTIVE_STATUS = "all_good";
export const ACTIVES_MEASURE_KEY = "actives";

export type OutcomeTreatmentItem = {
    id: number | string;
    treatment_status_new: string | null;
    age_when_started: number | null;
    date_of_start: string | null;
    date_if_finish: string | null;
    diagnose_main?: { id: number | string; name?: string | null } | null;
    client?: { client_id?: string | null } | null;
    client_owner?: { id?: number | string; user_id?: { id?: string; first_name: string | null; last_name: string | null } | null } | null;
    sessions?: Array<{ service: number | null; status: string | null }> | null;
};

export type OutcomeFilters = { groupKey: CoarseDisorderGroupKey | null; clientType: ClientTypeKey | null };

export const outcomeOwnerNameOf = (treatment: OutcomeTreatmentItem) => {
    const ownerUser = treatment.client_owner?.user_id;
    const fullName = [ownerUser?.first_name, ownerUser?.last_name].filter(Boolean).join(" ").trim();
    const ownerName = fullName || NO_OWNER_LABEL;
    return ownerName;
};

const doneNfbPsySessionsCount = (treatment: OutcomeTreatmentItem) => {
    const sessionList = treatment.sessions || [];
    const count = sessionList.filter(
        (session) => session.status === "done" && (Number(session.service) === NFB_SERVICE || Number(session.service) === PSY_SERVICE)
    ).length;
    return count;
};

const hasFinishedStatus = (treatment: OutcomeTreatmentItem) =>
    Boolean(treatment.treatment_status_new && FINISHED_STATUSES.includes(treatment.treatment_status_new));
const hasActiveStatus = (treatment: OutcomeTreatmentItem) => treatment.treatment_status_new === ACTIVE_STATUS;

export const finishedTreatmentsOf = (treatments: Array<OutcomeTreatmentItem>) => treatments.filter(hasFinishedStatus);
export const activeTreatmentsOf = (treatments: Array<OutcomeTreatmentItem>) => treatments.filter(hasActiveStatus);

const matchesOutcomeFilters = (treatment: OutcomeTreatmentItem, filters: OutcomeFilters) => {
    if (filters.groupKey !== null && coarseDisorderGroupOf(treatment.diagnose_main?.id) !== filters.groupKey) return false;
    if (filters.clientType !== null && clientTypeOfAge(treatment.age_when_started) !== filters.clientType) return false;
    return true;
};

// Itens do pivot = terminados-no-período ∪ ativos-snapshot, filtrados por grupo/tipo; as medidas separam
// por estado (Ativos = fotografia atual, não segue o "Período"; restantes = terminados no período).
export const outcomePivotItems = (
    finishedItems: Array<OutcomeTreatmentItem>,
    activeItems: Array<OutcomeTreatmentItem>,
    filters: OutcomeFilters
) => {
    const finished = finishedItems.filter(hasFinishedStatus).filter((treatment) => matchesOutcomeFilters(treatment, filters));
    const active = activeItems.filter(hasActiveStatus).filter((treatment) => matchesOutcomeFilters(treatment, filters));
    const items = [...finished, ...active];
    return items;
};

const successCountOf = (treatments: Array<OutcomeTreatmentItem>) =>
    treatments.filter((treatment) => SUCCESS_STATUSES.includes(treatment.treatment_status_new!)).length;

const successPctOf = (treatments: Array<OutcomeTreatmentItem>) => {
    const successCount = successCountOf(treatments);
    const pct = successRatePct(successCount, treatments.length - successCount);
    return pct;
};

const clientTypeTreatmentsOf = (treatments: Array<OutcomeTreatmentItem>, clientType: ClientTypeKey) =>
    treatments.filter((treatment) => clientTypeOfAge(treatment.age_when_started) === clientType);

const avgDoneSessionsOf = (treatments: Array<OutcomeTreatmentItem>) => {
    if (treatments.length === 0) return null;
    const doneSum = treatments.reduce((sum, treatment) => sum + doneNfbPsySessionsCount(treatment), 0);
    const average = doneSum / treatments.length;
    return average;
};

const formatCount = (value: number | null) => (value === null ? "—" : formatQuantity(value));
const formatPct = (value: number | null) => (value === null ? "—" : formatPercent(value, 1));

// Um tipo de cliente específico torna as colunas de split (Adultos/Crianças) redundantes → saem.
export const outcomeMeasures = (clientType: ClientTypeKey | null): Array<PivotMeasure<OutcomeTreatmentItem>> => {
    const finishedOf = (cellItems: Array<OutcomeTreatmentItem>) => finishedTreatmentsOf(cellItems);
    const baseMeasures: Array<PivotMeasure<OutcomeTreatmentItem>> = [
        { key: ACTIVES_MEASURE_KEY, label: "Ativos", isNumeric: true, compute: (cellItems) => activeTreatmentsOf(cellItems).length, format: formatCount },
        { key: "finished", label: "Terminados", isNumeric: true, compute: (cellItems) => finishedOf(cellItems).length, format: formatCount },
        { key: "successPct", label: "% sucesso", isNumeric: true, compute: (cellItems) => successPctOf(finishedOf(cellItems)), format: formatPct },
    ];
    const splitMeasures: Array<PivotMeasure<OutcomeTreatmentItem>> = [
        { key: "adults", label: "Adultos", isNumeric: true, compute: (cellItems) => clientTypeTreatmentsOf(finishedOf(cellItems), "adults").length, format: formatCount },
        { key: "children", label: "Crianças", isNumeric: true, compute: (cellItems) => clientTypeTreatmentsOf(finishedOf(cellItems), "children").length, format: formatCount },
    ];
    const averageMeasures: Array<PivotMeasure<OutcomeTreatmentItem>> = [
        { key: "avgSessions", label: "Média sessões", isNumeric: true, compute: (cellItems) => avgDoneSessionsOf(finishedOf(cellItems)), format: (value) => (value === null ? "—" : formatDecimal(value, 1)) },
    ];
    const measures = clientType === null ? [...baseMeasures, ...splitMeasures, ...averageMeasures] : [...baseMeasures, ...averageMeasures];
    return measures;
};

// Gestoras por nº de terminados desc; "Sem gestora de caso" sempre no fim; grupos de perturbação (2º
// nível) ficam alfabéticos (defeito do buildPivot).
const byFinishedDescending = (firstNode: PivotNode<OutcomeTreatmentItem>, secondNode: PivotNode<OutcomeTreatmentItem>) => {
    if (firstNode.label === NO_OWNER_LABEL && secondNode.label !== NO_OWNER_LABEL) return 1;
    if (secondNode.label === NO_OWNER_LABEL && firstNode.label !== NO_OWNER_LABEL) return -1;
    const firstFinished = finishedTreatmentsOf(firstNode.items).length;
    const secondFinished = finishedTreatmentsOf(secondNode.items).length;
    const comparison = secondFinished - firstFinished || firstNode.label.localeCompare(secondNode.label);
    return comparison;
};

export const outcomePivot = (items: Array<OutcomeTreatmentItem>, clientType: ClientTypeKey | null) => {
    const pivot = buildPivot({
        items,
        rowLevels: [outcomeOwnerNameOf, (treatment) => coarseDisorderGroupLabel(coarseDisorderGroupOf(treatment.diagnose_main?.id))],
        measures: outcomeMeasures(clientType),
        sortRowsBy: [byFinishedDescending],
    });
    return pivot;
};

export type OutcomeDrillRow = {
    id: number | string;
    clientNumber: string | null;
    ownerName: string;
    clientTypeLabel: string;
    disorderLabel: string;
    statusNew: string | null;
    dateOfStart: string | null;
    dateIfFinish: string | null;
    doneSessions: number;
};

export const outcomeDrillRowOf = (treatment: OutcomeTreatmentItem) => {
    const row: OutcomeDrillRow = {
        id: treatment.id,
        clientNumber: treatment.client?.client_id ?? null,
        ownerName: outcomeOwnerNameOf(treatment),
        clientTypeLabel: CLIENT_TYPE_LABELS[clientTypeOfAge(treatment.age_when_started)],
        disorderLabel: disorderDrillLabelOf(treatment.diagnose_main),
        statusNew: treatment.treatment_status_new,
        dateOfStart: treatment.date_of_start,
        dateIfFinish: treatment.date_if_finish,
        doneSessions: doneNfbPsySessionsCount(treatment),
    };
    return row;
};
